// src/utils/achievementUtils.js
import { achievementData } from '../data/data';

/**
 * Maps monster keys to achievement stat names
 * @param {string} monsterKey - The monster key from combat
 * @returns {string|null} The corresponding achievement stat name, or null if no mapping exists
 */
export const getAchievementStatKey = (monsterKey) => {
  const monsterToStatMapping = {
    // Slimes
    'slime': 'slimesKilled',
    
    // Goblins (all goblin variants map to goblinsKilled)
    'goblinBasic': 'goblinsKilled',
    'goblinWarrior': 'goblinsKilled',
    'armoredGoblin': 'goblinsKilled',
    'goblinForager': 'goblinsKilled',
    'caveGoblinThug': 'goblinsKilled',
    'hobgoblinLeader': 'goblinsKilled',
    'hobgoblinGrunt': 'goblinsKilled',
    'hobgoblinArcher': 'goblinsKilled',
    'hobgoblinEnforcer': 'goblinsKilled',
    'gloomwoodBanditScout': 'goblinsKilled',
    
    // Bats
    'forestBat': 'batsKilled',
    'deepLurkerBat': 'batsKilled',
    
    // Boars
    'wildBoar': 'boarsKilled',
    
    // Wolves
    'wolfPup': 'wolvesKilled',
    'gloomfangWolfPup': 'wolvesKilled',
    'krellGloomfangAlpha': 'wolvesKilled',
    
    // Spiders
    'giantSpider': 'spidersKilled',
    'giantForestSpider': 'spidersKilled',
    
    // Beetles
    'caveBeetle': 'beetlesKilled',
    
    // Bears
    'caveBear': 'bearsKilled',
    
    // Other creatures that might not have specific achievements yet
    // but should still be tracked for potential future achievements
    'smallRockGolem': 'golemsKilled',
    'mediumIronGolem': 'golemsKilled',
    'lootGolem': 'golemsKilled',
    'ancientGolem': 'golemsKilled',
    'rockGolem': 'golemsKilled',
    'ironGolem': 'golemsKilled',
    'banditThug': 'banditsKilled',
    'banditRogue': 'banditsKilled',
    'earthElemental': 'elementalsKilled',
    'ghost': 'ghostsKilled',
    'specter': 'ghostsKilled',
    'corruptedWerewolf': 'werewolvesKilled',
    'dragonkinWhelp': 'dragonsKilled',
    'giantCaveCrab': 'crabsKilled',
    'murkwaterSalamander': 'salamandersKilled'
  };
  
  return monsterToStatMapping[monsterKey] || null;
};

/**
 * Updates a player statistic and returns the updated playerStats object
 * @param {Object} playerStats - Current player statistics
 * @param {string} statName - Name of the statistic to update
 * @param {number} value - Value to add to the statistic (use negative for subtraction)
 * @param {boolean} isAbsolute - If true, sets the value instead of adding to it
 * @returns {Object} Updated playerStats object
 */
export const updatePlayerStat = (playerStats, statName, value, isAbsolute = false) => {
  if (!playerStats) return playerStats;
  
  const newStats = { ...playerStats };
  
  if (isAbsolute) {
    newStats[statName] = value;
  } else {
    newStats[statName] = (newStats[statName] || 0) + value;
  }
  
  return newStats;
};

/**
 * Updates multiple statistics at once
 * @param {Object} playerStats - Current player statistics
 * @param {Object} updates - Object with stat names as keys and values to add
 * @returns {Object} Updated playerStats object
 */
export const updatePlayerStats = (playerStats, updates) => {
  if (!playerStats || !updates) return playerStats;
  
  let newStats = { ...playerStats };
  
  for (const [statName, value] of Object.entries(updates)) {
    newStats[statName] = (newStats[statName] || 0) + value;
  }
  
  return newStats;
};

/**
 * Checks which achievements have been completed and returns newly completed ones
 * @param {Object} playerStats - Current player statistics
 * @param {Array} completedAchievements - Array of already completed achievement IDs
 * @returns {Array} Array of newly completed achievement objects
 */
export const checkForNewAchievements = (playerStats, completedAchievements = []) => {
  if (!playerStats) return [];
  
  const newCompletions = [];
  
  for (const achievement of Object.values(achievementData)) {
    // Skip if already completed
    if (completedAchievements.includes(achievement.id)) continue;
    
    // Check if prerequisite is met (if any)
    if (achievement.prerequisiteAchId && !completedAchievements.includes(achievement.prerequisiteAchId)) {
      continue;
    }
    
    // Check if the target stat meets the requirement
    const currentValue = playerStats[achievement.targetStat] || 0;
    if (currentValue >= achievement.targetValue) {
      newCompletions.push(achievement);
    }
  }
  
  return newCompletions;
};

/**
 * Updates daily statistics and resets daily counters if needed
 * @param {Object} playerStats - Current player statistics
 * @param {boolean} isNewDay - Whether this is a new day (resets daily stats)
 * @returns {Object} Updated playerStats object
 */
export const updateDailyStats = (playerStats, isNewDay = false) => {
  if (!playerStats) return playerStats;
  
  const newStats = { ...playerStats };
  
  if (isNewDay) {
    // Update max stats from current day before resetting
    if (newStats.currentDayStats) {
      const dayStats = newStats.currentDayStats;
      
      // Update maximums
      newStats.maxActionsPerDay = Math.max(newStats.maxActionsPerDay || 0, dayStats.actionsPerformed || 0);
      newStats.maxToolsBreakInDay = Math.max(newStats.maxToolsBreakInDay || 0, dayStats.toolsBroken || 0);
      newStats.maxClicksPerDay = Math.max(newStats.maxClicksPerDay || 0, dayStats.minigameClicks || 0);
      
      // Check for energy efficiency
      const lowestEnergyPercent = dayStats.lowestEnergyPercent || 100;
      if (lowestEnergyPercent >= 50) {
        newStats.energyEfficientDays = (newStats.energyEfficientDays || 0) + 1;
      }
      
      // Check for perfect day
      if (dayStats.minigameAttempts > 0 && dayStats.minigamePerfectScores === dayStats.minigameAttempts) {
        newStats.perfectDays = (newStats.perfectDays || 0) + 1;
      }
      
      // Check for early/late start times
      if (dayStats.startTime) {
        const hour = new Date(dayStats.startTime).getHours();
        if (hour < 6) {
          newStats.timesStartedEarly = (newStats.timesStartedEarly || 0) + 1;
        }
      }
      
      if (dayStats.endTime) {
        const hour = new Date(dayStats.endTime).getHours();
        if (hour >= 24) { // Past midnight
          newStats.timesStayedUpLate = (newStats.timesStayedUpLate || 0) + 1;
        }
      }
    }
    
    // Reset daily counters
    newStats.currentDayStats = {
      actionsPerformed: 0,
      toolsBroken: 0,
      minigameClicks: 0,
      minigamePerfectScores: 0,
      minigameAttempts: 0,
      lowestEnergyPercent: 100,
      startTime: Date.now(),
      endTime: null,
    };
  }
  
  return newStats;
};

/**
 * Updates skill-related statistics
 * @param {Object} playerStats - Current player statistics
 * @param {Object} playerSkills - Current player skills object
 * @returns {Object} Updated playerStats object
 */
export const updateSkillStats = (playerStats, playerSkills) => {
  if (!playerStats || !playerSkills) return playerStats;
  
  const newStats = { ...playerStats };
  
  // Calculate max and min skill levels
  const skillLevels = Object.values(playerSkills).map(skill => skill.level || 1);
  newStats.maxSkillLevel = Math.max(...skillLevels);
  newStats.minSkillLevel = Math.min(...skillLevels);
  
  // Check if all stats are at 100 (for centurion achievement)
  const allStatsAt100 = Object.values(playerSkills).every(skill => (skill.level || 1) >= 100) ? 1 : 0;
  newStats.allStatsAt100 = allStatsAt100;
  
  return newStats;
};

/**
 * Updates building-related statistics
 * @param {Object} playerStats - Current player statistics
 * @param {Object} buildings - Current player buildings object
 * @returns {Object} Updated playerStats object
 */
export const updateBuildingStats = (playerStats, buildings) => {
  if (!playerStats || !buildings) return playerStats;
  
  const newStats = { ...playerStats };
  
  // Count owned buildings
  const ownedBuildings = Object.values(buildings).filter(building => building.owned);
  newStats.buildingsPurchased = ownedBuildings.length;
  
  // Count total upgrades
  const totalUpgrades = ownedBuildings.reduce((sum, building) => sum + (building.upgradeLevel || 0), 0);
  newStats.buildingUpgrades = totalUpgrades;
  
  // Count max level buildings (assuming max level is 4)
  const maxLevelBuildings = ownedBuildings.filter(building => (building.upgradeLevel || 0) >= 4).length;
  newStats.maxLevelBuildings = maxLevelBuildings;
  
  return newStats;
};

/**
 * Tracks combat-related statistics
 * @param {Object} playerStats - Current player statistics
 * @param {Object} combatResult - Combat result data
 * @returns {Object} Updated playerStats object
 */
export const updateCombatStats = (playerStats, combatResult) => {
  if (!playerStats || !combatResult) return playerStats;
  
  const newStats = { ...playerStats };
  
  if (combatResult.outcome === 'victory') {
    newStats.combatVictories = (newStats.combatVictories || 0) + 1;
    newStats.currentWinStreak = (newStats.currentWinStreak || 0) + 1;
    newStats.maxWinStreak = Math.max(newStats.maxWinStreak || 0, newStats.currentWinStreak);
  } else if (combatResult.outcome === 'defeat') {
    newStats.combatDefeats = (newStats.combatDefeats || 0) + 1;
    newStats.currentWinStreak = 0;
    
    if (combatResult.died) {
      newStats.combatDeaths = (newStats.combatDeaths || 0) + 1;
    }
  }
  
  // Track damage dealt
  if (combatResult.damageDealt) {
    newStats.totalDamageDealt = (newStats.totalDamageDealt || 0) + combatResult.damageDealt;
    newStats.maxDamageDealt = Math.max(newStats.maxDamageDealt || 0, combatResult.damageDealt);
  }
  
  // Track low health survival
  if (combatResult.survivedAtLowHealth) {
    newStats.lowHealthSurvives = (newStats.lowHealthSurvives || 0) + 1;
  }
  
  // Track monster kills
  if (combatResult.monstersDefeated) {
    for (const monsterKey of combatResult.monstersDefeated) {
      // Map monster keys to achievement stat names
      const statKey = getAchievementStatKey(monsterKey);
      if (statKey) {
        newStats[statKey] = (newStats[statKey] || 0) + 1;
      }
      newStats.totalMonstersKilled = (newStats.totalMonstersKilled || 0) + 1;
    }
  }
  
  return newStats;
};

/**
 * Tracks minigame statistics
 * @param {Object} playerStats - Current player statistics
 * @param {Object} minigameResult - Minigame result data
 * @returns {Object} Updated playerStats object
 */
export const updateMinigameStats = (playerStats, minigameResult) => {
  if (!playerStats || !minigameResult) return playerStats;
  
  const newStats = { ...playerStats };
  
  const { type, isPerfect, clicks } = minigameResult;
  
  // Update session completion counts
  const sessionStat = `${type}SessionsCompleted`;
  newStats[sessionStat] = (newStats[sessionStat] || 0) + 1;
  
  // Update perfect score counts
  if (isPerfect) {
    const perfectStat = `perfect${type.charAt(0).toUpperCase() + type.slice(1)}Scores`;
    newStats[perfectStat] = (newStats[perfectStat] || 0) + 1;
    
    // Update daily perfect scores
    if (newStats.currentDayStats) {
      newStats.currentDayStats.minigamePerfectScores = (newStats.currentDayStats.minigamePerfectScores || 0) + 1;
    }
  }
  
  // Update daily minigame stats
  if (newStats.currentDayStats) {
    newStats.currentDayStats.minigameAttempts = (newStats.currentDayStats.minigameAttempts || 0) + 1;
    newStats.currentDayStats.minigameClicks = (newStats.currentDayStats.minigameClicks || 0) + (clicks || 0);
  }
  
  return newStats;
};

/**
 * Tracks inventory and carry capacity statistics
 * @param {Object} playerStats - Current player statistics
 * @param {number} currentWeight - Current carry weight
 * @param {number} maxWeight - Maximum carry capacity
 * @returns {Object} Updated playerStats object
 */
export const updateInventoryStats = (playerStats, currentWeight, maxWeight) => {
  if (!playerStats) return playerStats;
  
  const newStats = { ...playerStats };
  
  // Check if at max capacity
  if (currentWeight >= maxWeight) {
    newStats.timesReachedMaxCapacity = (newStats.timesReachedMaxCapacity || 0) + 1;
  }
  
  return newStats;
};

/**
 * Tracks energy efficiency for the current day
 * @param {Object} playerStats - Current player statistics
 * @param {number} currentEnergyPercent - Current energy as percentage (0-100)
 * @returns {Object} Updated playerStats object
 */
export const updateEnergyStats = (playerStats, currentEnergyPercent) => {
  if (!playerStats) return playerStats;
  
  const newStats = { ...playerStats };
  
  if (newStats.currentDayStats) {
    newStats.currentDayStats.lowestEnergyPercent = Math.min(
      newStats.currentDayStats.lowestEnergyPercent || 100,
      currentEnergyPercent
    );
  }
  
  return newStats;
};

/**
 * Tracks when tools break
 * @param {Object} playerStats - Current player statistics
 * @returns {Object} Updated playerStats object
 */
export const updateToolBreakStats = (playerStats) => {
  if (!playerStats) return playerStats;
  
  const newStats = { ...playerStats };
  
  if (newStats.currentDayStats) {
    newStats.currentDayStats.toolsBroken = (newStats.currentDayStats.toolsBroken || 0) + 1;
  }
  
  return newStats;
};

/**
 * Tracks daily actions performed
 * @param {Object} playerStats - Current player statistics
 * @returns {Object} Updated playerStats object
 */
export const updateActionStats = (playerStats) => {
  if (!playerStats) return playerStats;
  
  const newStats = { ...playerStats };
  
  if (newStats.currentDayStats) {
    newStats.currentDayStats.actionsPerformed = (newStats.currentDayStats.actionsPerformed || 0) + 1;
  }
  
  return newStats;
};

/**
 * Helper function to format achievement progress for display
 * @param {Object} achievement - Achievement object
 * @param {number} currentValue - Current progress value
 * @returns {Object} Formatted progress information
 */
export const formatAchievementProgress = (achievement, currentValue) => {
  const progress = Math.min(currentValue / achievement.targetValue, 1);
  const percentage = Math.round(progress * 100);
  
  return {
    progress,
    percentage,
    currentValue,
    targetValue: achievement.targetValue,
    isCompleted: currentValue >= achievement.targetValue,
    remaining: Math.max(0, achievement.targetValue - currentValue)
  };
};

/**
 * Gets achievements by category
 * @param {string} category - Category to filter by
 * @returns {Array} Array of achievements in the category
 */
export const getAchievementsByCategory = (category) => {
  return Object.values(achievementData).filter(achievement => achievement.category === category);
};

/**
 * Gets all achievement categories
 * @returns {Array} Array of unique category names
 */
export const getAchievementCategories = () => {
  return [...new Set(Object.values(achievementData).map(achievement => achievement.category))];
};

/**
 * Calculates total gold earned from completed achievements
 * @param {Array} completedAchievements - Array of completed achievement IDs
 * @returns {number} Total gold earned from achievements
 */
export const calculateAchievementGoldEarned = (completedAchievements = []) => {
  return completedAchievements.reduce((total, achievementId) => {
    const achievement = achievementData[achievementId];
    return total + (achievement?.goldReward || 0);
  }, 0);
}; 